import { 
	createSlice,
	createAsyncThunk,
	createEntityAdapter
} from '@reduxjs/toolkit'

import {
	envAwarePost
} from '../../util/util'

export const getWeeklyCompliance = createAsyncThunk(
	'getWeeklyCompliance',
	async (payload) => envAwarePost('api/fit-stats/api.php',
	{
		action:'getWeeklyCompliance',
		...payload
	})
)

export const getMonthlyCompliance = createAsyncThunk(
	'getMonthlyCompliance',
	async (payload) => envAwarePost('api/fit-stats/api.php',{
		action:'getMonthlyCompliance',
		...payload
	})
)

export const getComplianceInRange = createAsyncThunk(
	'getComplianceInRange',
	async payload => envAwarePost('api/fit-stats/api.php',{
		action:'getComplianceInRange',
		...payload
	})
)

const trainersAdapter = createEntityAdapter({
	selectId:entity=>entity.user_id_number
})

const clientsAdapter = createEntityAdapter({
	selectId:entity=>entity.user_id_number
})

const emptyData = ()=>({
	gym:{
		compliance:{},
		trainer_ids:[],
		client_ids:[],
		shared_clients:{
			ids:[]
		}
	},
	chain:{
		compliance:{},
		gym_ids:[],
		trainer_ids:[],
		client_ids:[],
	},
	gym_map:{},
	trainer_map:{},
	client_map:{},
})

const complianceSlice = createSlice({
	name:'compliance',
	initialState:{
		status:'init',
		filters:{
			hideNoData:false,
			eventType:'all',
			dateRange:'weekly',
			account:{
				type:'init',
				constraints:{
					//which gyms to show sub accounts for
					gym:'all',
					//which trainers to show sub accounts for
					trainer:'all'
				}
			},
		},
		weekly:emptyData(),
		monthly:emptyData(),
		custom:emptyData(),
	},
	reducers:{
		updateDateRange:(state,action)=>{
			state.filters.dateRange = action.payload
		}, 
		updateAccountFilterType:(state,action)=>{
			state.filters.account.type = action.payload
		},
		updateAccountFilterConstraints:(state,action)=>{
			const {type,constraint} = action.payload
			state.filters.account.constraints[type] = constraint
			const currentTrainerConstraint = state.filters.account.constraints.trainer
			if(type==='gym'&&!['all','shared'].includes(currentTrainerConstraint)){
				state.filters.account.constraints.trainer = 'all'
			}
		},
		updateFilterEventType:(state,action)=>{
			state.filters.eventType = action.payload
		},
		toggleNoDataFilter: state => {
			state.filters.hideNoData = !state.filters.hideNoData
		}
	},
	extraReducers:{
		[getWeeklyCompliance.pending] : state => {
			state.status = 'loading'
		},

		[getWeeklyCompliance.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			state.status = 'idle'
			state.weekly = data
		},

		[getMonthlyCompliance.pending] : state => {
			state.status = 'loading'
		},

		[getMonthlyCompliance.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			state.status = 'idle'
			state.monthly = data
		},

		[getComplianceInRange.pending] : state => {
			state.status = 'loading'
		},

		[getComplianceInRange.fulfilled] : (state,action) => {
			const {success,data} = action.payload
			state.status = 'idle'
			state.custom = data
		},
	}
})

export const {
	updateDateRange,
	updateAccountFilterType,
	updateFilterEventType,
	updateAccountFilterConstraints,
	toggleNoDataFilter
} = complianceSlice.actions

export const selectComplianceData = (state)=>{
	const dateRange = state.compliance.filters.dateRange
	return state.compliance[dateRange]
}

export const selectGymFilters = state=>{
	const currentUserAccountType =  state.users.current_user.account_type
	if(currentUserAccountType === 'chain'){
		return state.compliance.weekly.chain.gym_ids.map(
			gym_id=>state.compliance.weekly.gym_map[gym_id]
		)
	}else{
		return []
	}
}


/**
 * Selects trainers used to filter clients by trainer
 * If a gym is selected it will only return trainers for that gym
 * Else returns all trainers in chain
 * NOTE: only chains will ever get to the else case 
 * (ie gym accounts will always have gymConstraint = 'all')
 */
export const selectTrainerFilters = state=>{
	const rangeData = state.compliance.weekly
	const gymConstraint = state.compliance.filters.account.constraints.gym
	const currentUserAccountType = state.users.current_user.account_type
	if(gymConstraint==='all'){
		return state.compliance.weekly[currentUserAccountType].trainer_ids.map(
			trainer_id=>state.compliance.weekly.trainer_map[trainer_id]
		)
	}else{
		return state.compliance.weekly.gym_map[gymConstraint].trainer_ids.map(
			trainer_id=>state.compliance.weekly.trainer_map[trainer_id]
		)
	}
}

export const complianceNotEmpty = (data,displayKeys)=>(
	displayKeys.map((key)=>{
		const chartData = (
			key !== 'simplified_nutrition' ?
			[
				parseInt(data[key]?.complete||0),
				parseInt(data[key]?.open||0),
				parseInt(data[key]?.notMet||0)
			] : [
				parseInt(data[key]?.completed||0),
				parseInt(data[key]?.assigned||0)-parseInt(data[key]?.completed||0),
			]
		)
		return chartData.reduce((accumulator, currentValue) => accumulator + currentValue)
	}).reduce((accumulator, currentValue) => accumulator + currentValue)
)

export const selectDisplayKeys = (state)=>(
	{
		all:['workout','task','nutrition','simplified_nutrition'],
		workout:['workout'],
		task:['task'],
		nutrition:['nutrition'],
		simplified_nutrition:['simplified_nutrition']
	}[state.compliance.filters.eventType]
)

/**
 * Complex selector that contains all feature logic for 
 * Filtering compliance. Returns all compliance data that
 * should get mapped over by the Drilldown component
 */
export const selectCompliance = (state)=>{
	const displayKeys = selectDisplayKeys(state)
	const hideNoData = state.compliance.filters.hideNoData
	const {dateRange,account} = state.compliance.filters
	const currentUserAccountType = state.users.current_user.account_type
	//get data for the date range selected
	const rangeData = state.compliance[dateRange]
	const gymConstraint = account.constraints.gym
	const trainerConstraint = account.constraints.trainer
	let data = []
	/*
		trainer & client filtering works the same for gyms & chains
		so no seperate control flow is needed. GOTCHA: The filter
		after the map DOES work differently for gyms & chains
	*/
	if(account.type === 'trainer'){
		//filter and map all trainers
		data = rangeData[currentUserAccountType].trainer_ids.map(trainer_id=>{
			const dat = {...rangeData.trainer_map[trainer_id]}
			dat.account_id = trainer_id
			return dat
		}).filter(trainer=>(gymConstraint === 'all' || trainer.gym_id==gymConstraint))
	}else if(account.type === 'client'){
		//filter and map over all clients
		//WARNING: complex filter for trainer/gym & shared client contraints
		data = rangeData[currentUserAccountType].client_ids.map(client_id=>{
			const dat = {...rangeData.client_map[client_id]}
			dat.account_id = client_id
			return dat
		}).filter(client=>(
			//all trainers or is client of selected trainer AND
			(trainerConstraint === 'all' || client.user_trainer_id==trainerConstraint) &&
			//all gyms oor client is member of selected gym AND
			(gymConstraint === 'all' || client.gym_id==gymConstraint) || 
			//looking for clients that share trainers and are a member of all gyms or selected gym
			(
				//if looking for shared clients
				trainerConstraint === 'shared' && 
				//is a shared client check
				client.gym_id == client.user_trainer_id &&
				//member of selected gym or if we are looking at all gyms
				( client.gym_id == gymConstraint || gymConstraint == 'all')
			)
		))
	}else if(currentUserAccountType === 'chain'){
		//logic that only pertains to chain/coroporate gym accounts
		if(account.type === 'chain'){
			//put chain agg in array since the generalized drilldown component
			//maps over an array
			data = [rangeData.chain]
		}else if(account.type === 'gym'){
			data = rangeData.chain.gym_ids.map(gym_id=>{
				const dat = {...rangeData.gym_map[gym_id]}
				dat.account_id = gym_id
				return dat
			})
		}
	}else if(currentUserAccountType === 'gym'){
		//logic only valid for gyms
		data = [rangeData.gym]
	}
	//filter data to remove accounts with no data
	if(hideNoData) data = data.filter(dat=>complianceNotEmpty(dat.compliance,displayKeys))
	//return data or empty array
	return data
}

export default complianceSlice.reducer